<template>
  <div class="banner-slider" :class="className">
    <!-- ✅ เพิ่ม loading state -->
    <div v-if="props.images.length === 0" class="banner-loading">
      <div class="loading-text">ລໍຖ້າແປັບໜຶ່ງເດີ້...</div>
    </div>
    <!-- ✅ แสดง slider เมื่อมีข้อมูลแล้ว -->
    <template v-else>
      <div
        class="slider-wrapper"
        @touchstart="onTouchStart"
        @touchmove.passive="onTouchMove"
        @touchend="onTouchEnd"
        @mouseenter="pauseAutoPlay"
        @mouseleave="resumeAutoPlay"
      >
        <transition-group name="fade" tag="div" class="slides-container">
          <div
            v-for="(slide, index) in slides"
            v-show="index === currentIndex"
            :key="index"
            class="slide"
          >
            <img
              :src="slide.url"
              :alt="slide.alt || `Slide ${index + 1}`"
              class="slide-image"
              :loading="index === 0 ? 'eager' : 'lazy'"
            />

            <div class="slide-overlay"></div>

            <div v-if="slide.title" class="slide-content">
              <h2 class="slide-title">{{ slide.title }}</h2>
              <p v-if="slide.description" class="slide-description">
                {{ slide.description }}
              </p>
            </div>
          </div>
        </transition-group>

        <template v-if="showNavigation && slides.length > 1">
          <button
            @click="goToPrevious"
            class="nav-button nav-button-prev"
            aria-label="Previous"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>

          <button
            @click="goToNext"
            class="nav-button nav-button-next"
            aria-label="Next"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </template>
      </div>

      <div v-if="showIndicators && slides.length > 1" class="indicators">
        <button
          v-for="(slide, index) in slides"
          :key="`indicator-${index}`"
          @click="goToSlide(index)"
          :class="['indicator', { active: index === currentIndex }]"
          :aria-label="`Go to slide ${index + 1}`"
        />
      </div>
    </template>
  </div>
</template>
 
<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue";

const props = defineProps({
  images: {
    type: Array,
    default: () => [],
  },
  autoPlayInterval: {
    type: Number,
    default: 5000,
  },
  showIndicators: {
    type: Boolean,
    default: true,
  },
  showNavigation: {
    type: Boolean,
    default: true,
  },
  className: {
    type: String,
    default: "",
  },
});

const slides = computed(() => props.images);

const currentIndex = ref(0);
const isAutoPlaying = ref(true);
const touchStart = ref(null);
const touchEnd = ref(null);
let autoPlayTimer = null;
onUnmounted(() => stopAutoPlay());

const goToSlide = (index) => {
  currentIndex.value = index;
  resetAutoPlay();
};

const goToPrevious = () => {
  currentIndex.value =
    (currentIndex.value - 1 + slides.value.length) % slides.value.length;
  resetAutoPlay();
};

const goToNext = () => {
  currentIndex.value = (currentIndex.value + 1) % slides.value.length;
  resetAutoPlay();
};

const startAutoPlay = () => {
  if (!isAutoPlaying.value) return;
  autoPlayTimer = setInterval(() => {
    goToNext();
  }, props.autoPlayInterval);
};

const stopAutoPlay = () => {
  if (autoPlayTimer) {
    clearInterval(autoPlayTimer);
    autoPlayTimer = null;
  }
};

const resetAutoPlay = () => {
  stopAutoPlay();
  isAutoPlaying.value = false;
  setTimeout(() => {
    isAutoPlaying.value = true;
    startAutoPlay();
  }, 10000);
};

const pauseAutoPlay = () => {
  isAutoPlaying.value = false;
  stopAutoPlay();
};

const resumeAutoPlay = () => {
  isAutoPlaying.value = true;
  startAutoPlay();
};

const minSwipeDistance = 50;

const onTouchStart = (e) => {
  touchEnd.value = null;
  touchStart.value = e.touches[0].clientX;
};

const onTouchMove = (e) => {
  touchEnd.value = e.touches[0].clientX;
};

const onTouchEnd = () => {
  if (!touchStart.value || !touchEnd.value) return;
  const distance = touchStart.value - touchEnd.value;
  const isLeftSwipe = distance > minSwipeDistance;
  const isRightSwipe = distance < -minSwipeDistance;
  if (isLeftSwipe) goToNext();
  else if (isRightSwipe) goToPrevious();
};

onMounted(() => startAutoPlay());
onUnmounted(() => stopAutoPlay());
</script>

<style scoped>
/* เพิ่มเข้าไปใน style scoped */
.banner-loading {
  width: 100%;
  padding-bottom: 41.67%; /* อัตราส่วนเดียวกับ slider */
  position: relative;
  background: linear-gradient(
    135deg,
    #fffee7 0%,
    #e7f3ff 40%,
    #e2ffe6 70%,
    #ffe7e7 100%
  );

  background-size: 200% 200%;
  animation: shimmer 3s ease-in-out infinite;
}

.loading-text {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Noto Sans Lao", sans-serif;
  font-size: 1.1rem;
  color: #94a3b8;
  letter-spacing: 0.03em;
}

@keyframes shimmer {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}
.banner-slider {
  position: relative;
  width: 100%;
  overflow: hidden;
  background-color: #f5f5f5;
}

.slider-wrapper {
  position: relative;
  width: 100%;
  padding-bottom: 41.67%;
}

.slides-container {
  position: absolute;
  inset: 0;
}

.slide {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.slide-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.slide-overlay {
  position: absolute;
  inset: 0;
}

.slide-content {
  position: absolute;
  bottom: 2rem;
  left: 2rem;
  color: white;
  z-index: 10;
  max-width: 600px;
}

.slide-title {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}

.slide-description {
  font-size: 1.125rem;
  opacity: 0.9;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
}

.nav-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 20;
  background-color: rgba(250, 250, 250, 0.8);
  color: #323d47;
  padding: 1rem;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.nav-button:hover {
  background-color: white;
  transform: translateY(-50%) scale(1.1);
}

.nav-button-prev {
  left: 1rem;
}
.nav-button-next {
  right: 1rem;
}

.indicators {
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  display: flex;
  gap: 0.5rem;
}

.indicator {
  width: 0.5rem;
  height: 0.5rem;
  border: none;
  border-radius: 9999px;
  background-color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.3s;
  padding: 0;
}

.indicator:hover {
  background-color: rgba(255, 255, 255, 0.75);
}

.indicator.active {
  width: 2.5rem;
  background-color: white;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.7s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (min-width: 769px) {
  .slide-title {
    font-size: 3rem;
  }
  .slide-description {
    font-size: 1.25rem;
  }
  .nav-button {
    padding: 1rem;
  }
}

@media (max-width: 768px) {
  .slide-content {
    bottom: 1rem;
    left: 1rem;
  }
  .banner-slider {
    padding-top: 4rem;
  }
  .slide-title {
    font-size: 1.5rem;
  }
  .slide-description {
    font-size: 0.875rem;
  }
  .nav-button {
    padding: 0.5rem;
  }
}

@media (max-width: 480px) {
  .banner-slider {
    padding-top: 3rem;
  }
}
</style>